import ReactDOM from "react-dom";
import { useEffect, useState, useRef } from "react";
import { Button, SubTitle, Dropdown, Spacer, Input, TagInput } from "@dspworkplace/ui";
import styled from "styled-components";
import { useRecoilValueLoadable } from "recoil";
import { ScrollyModalStyle, ScrollyOverlayStyle } from "../../../../components/Modal";
import { useSelectedStation } from "../../../../components/StationSelector";
import DayPicker from "../../../../components/Calendar/day";
import { getCompanyId, upload, useAuth, getDateFormat } from "../../../../Auth";
import IsGranted from "../../../../security/IsGranted";
import AccessCheck from "../../../../security/AccessCheck";
import { useDeviceType } from "../../../../hooks/useDeviceType";
import toast from "../../../../components/Toasts/toast";
import { GetIncidentLookup } from "../../../../domain/vehicle/api/getIncidents";
import { VMVendor } from "../../../../domain/vehicle_maintenance_vendor/state/VehicleMaintenanceVendor";
import { VehicleUploadDocuments } from "../../../../domain/vehicle/api/VehicleUploadDocuments";
import { CreateVehiclePlanTasks } from "../../../../domain/vehicle_maintenance_task/api/CreateVehiclePlanTasks";
import { GetVehicleLookup } from "../../../../domain/vehicle/api/getVehicles";
import "../../global.css";
import { useForm } from "react-hook-form";
import { DropUploader } from "../../../../components/DropUploader/index.js";
import { useReportIssue } from "../component/ReportIssueContext.js";

const ScrollyModalStyleV2 = styled(ScrollyModalStyle)`
    padding: 0;
    display: flex;
    flex-direction: column;
    max-width: 520px;
    width: 100%;
    height: 90vh;
    max-height: 90vh;
    overflow: hidden;
    border-radius: 8px;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const ScrollyOverlayStyleV2 = styled(ScrollyOverlayStyle)`
    background-color: #00000066;
    overflow: hidden;
    display: block;
`;

const MobilePageContainer = styled.div`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fafbfc;
  display: flex;
  flex-direction: column;
  z-index: 11;
`;

const VAN_STATUS_OPTIONS = [
    { name: "All (Non Archived)", value: "all" },
    { name: "Active", value: "Active" },
    { name: "Inactive", value: "Inactive" },
    { name: "Under Repair", value: "Under Repair" },
    { name: "Being Serviced", value: "Being Serviced" },
    { name: "Body Shop", value: "Body Shop" },
    { name: "Archived", value: "Archived" },
];
const AMZ_STATUS_OPTIONS = [
    { name: "Active", value: "Active" },
    { name: "Out of Service", value: "Out of Service" },
    { name: "Grounded", value: "Grounded" },
    { name: "Maintenance", value: "Maintenance" },
    { name: "Body Shop", value: "Body Shop" },
];
const PRIORITY_OPTIONS = [
    { name: "Critical", value: "Critical" },
    { name: "Urgent", value: "Urgent" },
    { name: "Soon", value: "Soon" },
    { name: "Can Wait", value: "Can_wait" },
    { name: "Unknown", value: "Unknown" },
];

const AVOID_PERMISSION_COMPANIES = new Set([85, 121, 221, 258, 254, 400]);

const ReportIssuePage = ({ cancel = () => { }, success = () => {}, defaultVehicle: vehicleProp }) => {
    const [selectedStation] = useSelectedStation();
    const [vehicles, setVehicles] = useState([{ id: "loading", vehicleId: "Loading...", disabled: true }]);
    const [incidents, setIncidents] = useState([{ name: "Loading...", value: "loading", disabled: true }]);
    const [types, setTypes] = useState([{ name: "Loading...", value: "loading", disabled: true }]);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { register, handleSubmit } = useForm();
    const { defaultVehicle } = useReportIssue() || {};
    const [form, setForm] = useState({
        vehicle: vehicleProp ?? defaultVehicle ?? null, description: "", photos: [],
        incidents: [], vanStatus: null, amzStatus: null,
        type: null, priority: null, category: null,
        pm_date: null, pm_mileage: "", vendor: null,
        estimate: "", cost: "",
    });

    useEffect(() => {
        register({ name: "documents" });
    }, [register]);

    const { isMobile } = useDeviceType();
    const { api } = useAuth();
    const checkAvoidPermission = AVOID_PERMISSION_COMPANIES.has(Number.parseInt(getCompanyId()));
    const vendorsLoadable = useRecoilValueLoadable(VMVendor(selectedStation));
    const vendors = vendorsLoadable.state === "hasValue" ? vendorsLoadable.contents : [];

    const isFormValid = form.vehicle && form.vanStatus && form.category && form.type && form.priority;
    const todayISO = new Date().toISOString().split("T")[0];
    const [showUploader, setShowUploader] = useState(true);
    const pendingCancel = useRef(false);
    const pendingSuccess = useRef(null);
    const Overlay = ScrollyOverlayStyleV2;
    const Modal = ScrollyModalStyleV2;
    const uploaderRef = useRef();

    const updateForm = (field, value) =>
        setForm(prev => ({ ...prev, [field]: typeof value === "function" ? value(prev[field]) : value }));

    const handleCancel = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopPropagation) e.stopPropagation();
        pendingCancel.current = true;
        setShowUploader(false);
    };

    useEffect(() => {
        const handler = (e) => { if (e.keyCode === 27) handleCancel(); };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, []);

    useEffect(() => {
        if (!showUploader && pendingCancel.current) {
            pendingCancel.current = false;
            requestAnimationFrame(() => cancel());
        }
        if (!showUploader && pendingSuccess.current) {
            const data = pendingSuccess.current;
            pendingSuccess.current = null;
            requestAnimationFrame(() => success(data));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showUploader]);


    useEffect(() => {
        if (!selectedStation) return;
        let cancelled = false;
        (async () => {
            try {
                const [vehicleRes, incidentRes, typeRes] = await Promise.all([
                    GetVehicleLookup({ station_id: selectedStation }),
                    GetIncidentLookup({ station_id: selectedStation }),
                    api.get("/api/v2/category", { params: { entity: "VehicleMaintenancePlanItem" } }),
                ]);
                if (cancelled) return;
                setVehicles(vehicleRes.data);
                setIncidents(incidentRes.map(i => ({ value: i.id, name: i.label })));
                setTypes(typeRes.data.map(t => ({ name: t.label, value: t.id, children: t.children })));
            } catch (err) {
                if (!cancelled) { setVehicles([]); setIncidents([]); setTypes([]); }
                console.error("Failed to load lookups", err);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedStation, api]);

    const uploadTaskDocuments = async () => {
        const files = uploaderRef.current?.files;

        if (!files?.length) return [];

        const formData = new FormData();
        Array.from(files).forEach(file => {
            formData.append("image[]", file);
        });

        const res = await upload(formData);
        const data =
            res?.data?.count === 1
                ? res.data.data
                : res?.data;

        return data.map(v => v.ObjectURL);
    };


    const onSubmit = async (data) => {
        console.log('data', data)
        try {
            setIsSubmitting(true);
            setLoading(true);
            const createPayload = {
                vehicleId: Number.parseInt(form.vehicle.value ?? form.vehicle.id, 10),
                priority: form.priority.value.toLowerCase().replace(" ", "_"),
                categoryId:   Number.parseInt(form.category.value, 10),
                quote: Number.parseFloat(form.estimate || 0),
                estimate_cost: Number.parseFloat(form.cost || 0),
                pm_date: form.pm_date || null,
                pm_mileage: Number.parseFloat(form.pm_mileage || 0),
                notes: form.description,
                amzFleetToolStatus: form.amzStatus?.value ?? null,
                incidents: form.incidents?.length ? form.incidents.map(i => i.value).join(",") : "",
                vendorId: form.vendor?.value ? Number.parseInt(form.vendor.value, 10) : null,
                vanStatus: form.vanStatus.value,
                stationId: Number.parseInt(selectedStation, 10),
            };
            const createResponse = await CreateVehiclePlanTasks(createPayload);
            const taskId = createResponse.data.id;
            const documentUrls = await uploadTaskDocuments();

            if (documentUrls.length) {
                await VehicleUploadDocuments({
                    id: taskId,
                    documents: documentUrls
                });
            }
            toast({ type: "success", title: "Success", content: "Vehicle plan task created successfully!" });
            // Call success() directly so the form closes immediately.
            success(createResponse.data);
        } catch (e) {
            console.error(e);
            toast({ type: "error", title: "Error", content: e?.response?.data?.message || e?.message || "Something went wrong." });
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (isMobile) return;

        const scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth;

        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = `${scrollbarWidth}px`;

        return () => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        };
    }, [isMobile]);


    const FormContent = (
        <>
            <Spacer top={3} />

            <div className="grid-1 dsp-dropdown">
                <Dropdown
                    placeholder="Select"
                    name="vehicle"
                    label="Vehicle ID"
                    Required
                    options={(vehicles || []).map(v => ({
                        name: v.vehicleId,
                        value: v.id
                    }))}
                    defaultValue={form.vehicle?.value ?? ""}
                    onChange={e =>
                        updateForm("vehicle", { value: e.value, name: e.name })
                    }
                    visibleOptionsQty={7}
                />
            </div>

            <div className="grid-1 dsp-taginput">
                <TagInput
                    placeholder="Select"
                    label="Incidents"
                    name="incidents"
                    options={(incidents || []).map(i => ({ name: i.name, value: i.value }))}
                    defaultValue={form.incidents || []}
                    onChange={(values) => updateForm("incidents", values)}
                    visibleOptionsQty={7}
                />
            </div>

            <div className="grid-1 dsp-dropdown">
                <Dropdown
                    placeholder="Select"
                    label="Van Status"
                    name="vanStatus"
                    Required={true}
                    options={VAN_STATUS_OPTIONS}
                    defaultValue={form.vanStatus?.value ?? ""}
                    onChange={e => updateForm("vanStatus", { value: e.value, name: e.name })}
                    visibleOptionsQty={7}
                />
            </div>

            <div className="grid-1 dsp-dropdown">
                <Dropdown
                    placeholder="Select"
                    label="AMZ Status"
                    name="amzStatus"
                    options={AMZ_STATUS_OPTIONS}
                    defaultValue={form.amzStatus?.value ?? ""}
                    onChange={e => updateForm("amzStatus", { value: e.value, name: e.name })}
                    visibleOptionsQty={7}
                />
            </div>

            <div className="grid-2">
                <Spacer inline right={2} className="dsp-dropdown">
                    <Dropdown
                        placeholder="Select"
                        name="type"
                        label="Type"
                        Required={true}
                        options={(types || []).map(t => ({ name: t.name, value: t.value, children: t.children }))}
                        defaultValue={form.type?.value ?? ""}
                        onChange={e => {
                            const selected = types.find(t => t.value === e.value);
                            setForm(prev => ({
                                ...prev,
                                type: { value: e.value, label: e.name, children: selected?.children },
                                category: null,
                                pm_date: e.name === "PM" ? todayISO : prev.pm_date,
                            }));
                        }}
                        visibleOptionsQty={7}
                    />
                </Spacer>
                <Spacer inline className="dsp-dropdown">
                    <Dropdown
                        placeholder="Select"
                        label="Priority"
                        name="priority"
                        Required={true}
                        options={PRIORITY_OPTIONS}
                        defaultValue={form.priority?.value ?? ""}
                        onChange={e => updateForm("priority", { value: e.value, name: e.name })}
                        visibleOptionsQty={7}
                    />
                </Spacer>
            </div>

            <div className="grid-1 dsp-dropdown">
                <Dropdown
                    placeholder={form.type ? "Select" : "Select a type first"}
                    label="Category"
                    name="category"
                    Required={true}
                    options={(form.type?.children || []).map(c => ({ name: c.label ?? c.name, value: c.id ?? c.value }))}
                    defaultValue={form.category?.value ?? ""}
                    onChange={e => updateForm("category", { value: e.value, name: e.name })}
                    visibleOptionsQty={7}
                />
            </div>

            {form.type?.label === "PM" && (
                <div className="grid-2">
                    <Spacer inline right={2}>
                        <DayPicker
                            name="pm_date"
                            label="PM Date"
                            size="small"
                            onChange={e => updateForm("pm_date", e)}
                            displayFormat={getDateFormat(selectedStation)}
                        />
                    </Spacer>
                    <Spacer inline>
                        <Input
                            name="pm_mileage"
                            label="PM Mileage"
                            size="small"
                            type="number"
                            value={form.pm_mileage}
                            onChange={e => updateForm("pm_mileage", e.target.value)}
                        />
                    </Spacer>
                </div>
            )}

            <div className="grid-1 dsp-dropdown">
                {checkAvoidPermission ? (
                    <Dropdown
                        placeholder="Select"
                        name="vendor"
                        label="Vendor"
                        options={(vendors || []).map(v => ({ name: v.label, value: v.value }))}
                        defaultValue={form.vendor?.value ?? ""}
                        onChange={e => updateForm("vendor", { value: e.value, name: e.name })}
                        visibleOptionsQty={7}
                    />
                ) : (
                    <IsGranted feature={AccessCheck.addons.VEHICLE_MAINTENANCE_PLAN}>
                        <Dropdown
                            placeholder="Select"
                            name="vendor"
                            label="Vendor"
                            options={(vendors || []).map(v => ({ name: v.label, value: v.value }))}
                            defaultValue={form.vendor?.value ?? ""}
                            onChange={e => updateForm("vendor", { value: e.value, name: e.name })}
                            visibleOptionsQty={7}
                        />
                    </IsGranted>
                )}
            </div>

            <div className="grid-2">
                <Spacer inline right={2}>
                    <Input
                        type="number"
                        label="Estimate"
                        value={form.estimate}
                        onChange={e => updateForm("estimate", e.target.value)}
                    />
                </Spacer>
                <Spacer inline>
                    <Input
                        type="number"
                        label="Cost"
                        value={form.cost}
                        onChange={e => updateForm("cost", e.target.value)}
                    />
                </Spacer>
            </div>

            <div className="grid-1">
                <Input
                    name="notes"
                    size="big"
                    label="Notes"
                    as="textarea"
                    value={form.description}
                    onChange={e => updateForm("description", e.target.value)}
                    style={{ height: 150, width: "100%" }}
                />
            </div>

            {!isMobile && showUploader && (
                <>
                    <div className="grid-1">
                        <div className="uploaderWrapper">
                            <DropUploader
                                name="documents"
                                ref={uploaderRef}
                                multiple
                                label="Document"
                                onChange={(items) => {
                                    updateForm("documents", items);
                                }}
                            />
                        </div>
                    </div>
                    <p
                        style={{
                            color: "#90a1b9",
                            fontSize: "12px",
                            marginTop: "8px"
                        }}
                    >
                        PDF, DOC, DOCX, JPG, or PNG. Max 25MB.
                    </p>
                </>
            )}
            <Spacer top={2} />
        </>
    );

    if (isMobile) {
        return (
            <MobilePageContainer>

                <header className="modalHeader">
                    <SubTitle
                        style={{
                            color: "#00538a",
                            fontSize: "20px",
                            fontWeight: 700
                        }}
                    >
                        New Vehicle Maintenance Task
                    </SubTitle>
                </header>

                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "20px"
                    }}
                >
                    {FormContent}
                </div>

                <footer className="modalFooter">
                    <Button type="cancel" onClick={handleCancel}>
                        Cancel
                    </Button>

                    <Button
                        type="primary"
                        disabled={!isFormValid || loading || isSubmitting}
                        onClick={handleSubmit(onSubmit)}
                    >
                        Save
                    </Button>
                </footer>

            </MobilePageContainer>
        );
    }

    return (
        <Overlay>
            <Modal onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit(onSubmit)} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <header className="modalHeader">
                        <SubTitle style={{ color: "#00538a", fontSize: "20px", fontWeight: 700 }}>
                            New Vehicle Maintenance Task
                        </SubTitle>
                    </header>
                    <div
                        style={{
                            flex: 1,
                            overflowY: "auto",
                            padding: "20px"
                        }}
                    >
                        {FormContent}
                    </div>

                    <footer className="modalFooter">
                        <Button type="cancel" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            disabled={!isFormValid || isSubmitting || loading}
                        >
                            Save
                        </Button>
                    </footer>
                </form>
            </Modal>
        </Overlay>
    );
};

const showReportIssueForm = (opts = {}) => {
    const modalRoot = document.getElementById("modal-root");

    const hide = () => {
        requestAnimationFrame(() => {
            setTimeout(() => {
                if (modalRoot) {
                    ReactDOM.unmountComponentAtNode(modalRoot);
                }
            }, 50);
        });
    };

    return new Promise((resolve, reject) => {
        const cancel = (data) => {
            hide();
            reject(data);
        };
        const success = (data) => {
            hide();
            resolve(data);
        };

        ReactDOM.render(
            <ReportIssuePage cancel={cancel} success={success} {...opts} />,
            modalRoot,
        );
    });
};

export { showReportIssueForm, ReportIssuePage };
export default ReportIssuePage;
